import { useCallback } from 'react';

import { AxiosResponse } from 'axios';

import { useSuspenseQuery } from '@tanstack/react-query';
import type { TypeGetWorkLocationsShuttleOperationsResponse } from '~/api/workLocations';
import { registerMockAPI } from '~/mocks/mockApis';

import { axiosInstance } from '~/services/fetchServiceV2';

/** @description 근무지별 셔틀 운행 조회 */
export const useGetShuttleSchedule = ({ workLocationId }: { workLocationId: string }) => {
  const handleResponse = useCallback(
    (response: AxiosResponse<TypeGetWorkLocationsShuttleOperationsResponse>) => response.data.data,
    [workLocationId]
  );

  const { data } = useSuspenseQuery({
    queryKey: ['shuttle-schedule', workLocationId],
    queryFn: async () => {
      const endpoint = `/work-locations/${workLocationId}/shuttle-operations`;
      return await axiosInstance.get<TypeGetWorkLocationsShuttleOperationsResponse>(endpoint);
    },
    gcTime: 0,
    retry: 1,
    select: (response) => handleResponse(response),
  });

  return { data };
};

// ─── Mock API ───

registerMockAPI<TypeGetWorkLocationsShuttleOperationsResponse>({
  method: 'get',
  endpoint: '/work-locations/:workLocationId/shuttle-operations',
  label: '셔틀버스 안내/노선 목록 조회',
  scenarios: [
    {
      name: '노선 목록 다중 (시간대 4개, 노선 4개)',
      response: {
        code: '0000',
        message: '정상',
        data: [
          {
            arrivalHour: 8,
            operations: [
              {
                id: 1,
                name: '가정역',
                stops: [
                  { id: 'stop-1-1', stopName: '가정역 1번 출구', stopTime: '06:50' },
                  { id: 'stop-1-2', stopName: '검암역 2번 출구', stopTime: '07:00' },
                  { id: 'stop-1-3', stopName: '계양역 1번 출구 맞은편 버스정류장', stopTime: '07:10' },
                  { id: 'stop-1-4', stopName: '김포 센터', stopTime: '08:00' },
                ],
                routeGuideLink: null,
              },
              {
                id: 2,
                name: '일산대화역',
                stops: [
                  { id: 'stop-2-1', stopName: '대화역 3번 출구', stopTime: '06:30' },
                  { id: 'stop-2-2', stopName: '풍산역 1번 출구', stopTime: '06:45' },
                  { id: 'stop-2-3', stopName: '김포 센터', stopTime: '08:00' },
                ],
                routeGuideLink: 'https://kurly.com',
              },
              {
                id: 3,
                name: '부평역',
                stops: [
                  { id: 'stop-3-1', stopName: '부평역 5번 출구', stopTime: '06:40' },
                  { id: 'stop-3-2', stopName: '부평구청역 2번 출구', stopTime: '06:50' },
                  { id: 'stop-3-3', stopName: '김포 센터', stopTime: '08:00' },
                ],
                routeGuideLink: 'https://kurly.com',
              },
            ],
          },
          {
            arrivalHour: 10,
            operations: [
              {
                id: 4,
                name: '가산역',
                stops: [
                  { id: 'stop-4-1', stopName: '가산디지털단지역 7번 출구', stopTime: '08:30' },
                  { id: 'stop-4-2', stopName: '구로디지털단지역 1번 출구', stopTime: '08:40' },
                  { id: 'stop-4-3', stopName: '김포 센터', stopTime: '10:00' },
                ],
                routeGuideLink: null,
              },
              {
                id: 5,
                name: '계산역',
                stops: [
                  { id: 'stop-5-1', stopName: '계산역 1번 출구', stopTime: '08:50' },
                  { id: 'stop-5-2', stopName: '임학역 1번 출구', stopTime: '09:00' },
                  { id: 'stop-5-3', stopName: '김포 센터', stopTime: '10:00' },
                ],
                routeGuideLink: 'https://kurly.com',
              },
            ],
          },
          {
            arrivalHour: 16,
            operations: [
              {
                id: 6,
                name: '가산역',
                stops: [
                  { id: 'stop-6-1', stopName: '가산디지털단지역 7번 출구', stopTime: '14:30' },
                  { id: 'stop-6-2', stopName: '구로디지털단지역 1번 출구', stopTime: '14:40' },
                  { id: 'stop-6-3', stopName: '김포 센터', stopTime: '16:00' },
                ],
                routeGuideLink: null,
              },
              {
                id: 7,
                name: '계산역',
                stops: [
                  { id: 'stop-7-1', stopName: '계산역 1번 출구', stopTime: '14:50' },
                  { id: 'stop-7-2', stopName: '임학역 1번 출구', stopTime: '15:00' },
                  { id: 'stop-7-3', stopName: '박촌역 1번 출구', stopTime: '15:05' },
                  { id: 'stop-7-4', stopName: '계양역 1번 출구 맞은편 버스정류장', stopTime: '15:15' },
                  { id: 'stop-7-5', stopName: '김포 센터', stopTime: '16:00' },
                ],
                routeGuideLink: null,
              },
            ],
          },
          {
            arrivalHour: 20,
            operations: [
              {
                id: 8,
                name: '구래역',
                stops: [
                  { id: 'stop-8-1', stopName: '구래역 2번 출구', stopTime: '18:30' },
                  { id: 'stop-8-2', stopName: '김포 센터', stopTime: '20:00' },
                ],
                routeGuideLink: null,
              },
              {
                id: 9,
                name: '사당역',
                stops: [
                  { id: 'stop-9-1', stopName: '사당역 4번 출구', stopTime: '18:20' },
                  { id: 'stop-9-2', stopName: '김포 센터', stopTime: '20:00' },
                ],
                routeGuideLink: 'https://kurly.com',
              },
            ],
          },
        ],
      } satisfies TypeGetWorkLocationsShuttleOperationsResponse,
    },
    {
      name: '노선 단일 (시간대 1개, 노선 1개)',
      response: {
        code: '0000',
        message: '정상',
        data: [
          {
            arrivalHour: 8,
            operations: [
              {
                id: 1,
                name: '가정역',
                stops: [
                  { id: 'stop-1-1', stopName: '가정역 1번 출구', stopTime: '06:50' },
                  { id: 'stop-1-2', stopName: '김포 센터', stopTime: '08:00' },
                ],
                routeGuideLink: 'https://kurly.com',
              },
            ],
          },
        ],
      } satisfies TypeGetWorkLocationsShuttleOperationsResponse,
    },
    {
      name: '노선 없음 (빈 목록)',
      response: {
        code: '0000',
        message: '정상',
        data: [],
      } satisfies TypeGetWorkLocationsShuttleOperationsResponse,
    },
    {
      name: '노선 단일 (빈 정류장)',
      response: {
        code: '0000',
        message: '정상',
        data: [
          {
            arrivalHour: 8,
            operations: [
              {
                id: 1,
                name: '가정역',
                stops: [],
                routeGuideLink: 'https://kurly.com',
              },
            ],
          },
        ],
      } satisfies TypeGetWorkLocationsShuttleOperationsResponse,
    },
    {
      name: '노선 단일 (긴 정류장명)',
      response: {
        code: '0000',
        message: '정상',
        data: [
          {
            arrivalHour: 8,
            operations: [
              {
                id: 1,
                name: '가정역',
                stops: [
                  {
                    id: 'stop-long-1',
                    stopName: '서울특별시 강남구 테헤란로 152 강남파이낸스센터 정문 앞 버스정류장 (긴 정류장명 테스트)',
                    stopTime: '06:50',
                  },
                  { id: 'stop-long-2', stopName: '김포 센터', stopTime: '08:00' },
                ],
                routeGuideLink: 'https://kurly.com',
              },
            ],
          },
        ],
      } satisfies TypeGetWorkLocationsShuttleOperationsResponse,
    },
    {
      name: '서버 에러 (500)',
      response: {
        code: '500',
        message: 'Internal Server Error',
        data: [],
      },
      status: 500,
    },
  ],
});
